<template>
  <div>
    <div class="thumbs-rating__actions">
      <v-popover trigger="hover" placement="top" delay="550" popoverClass="ai-tooltip">
        <div class="thumbs-rating__action">
          <BaseRoundButton
            :size="40"
            :class="thumbsDownCssClass"
            icon="thumbs-down"
            is-white
            :is-neutral="tender.tenantTender.thumbsRating === 'UP'"
            :is-red="tender.tenantTender.thumbsRating === 'DOWN'"
            @click.native.stop="onThumbsDown"
          />
          <BaseAvatar
            v-if="tender.tenantTender.thumbsRating === 'DOWN'"
            class="thumbs-rating__avatar"
            :name="getFullName(tender.tenantTender.thumbsRatingBy)"
            :color="isCurrentUser(tender.tenantTender.thumbsRatingBy) ? '#fcdbd9' : '#d9e5f9'"
            :small="true"
          />
        </div>
        <template v-slot:popover>
          <div class="thumbs-rating--tooltip">
            <BaseIcon name="creation" class="magic__icon" />
            <div class="thumbs-rating--tooltip__title">
              {{ thumbDownTooltip.title }}
            </div>
            <div class="thumbs-rating--tooltip__subtitle">
              {{ thumbDownTooltip.subtitle }}
            </div>
          </div>
        </template>
      </v-popover>
      <v-popover trigger="hover" placement="top" delay="550" popoverClass="ai-tooltip">
        <div class="thumbs-rating__action">
          <BaseRoundButton
            :size="40"
            :class="thumbsUpCssClass"
            icon="thumbs-up"
            is-white
            :is-neutral="tender.tenantTender.thumbsRating === 'DOWN'"
            :is-green="tender.tenantTender.thumbsRating === 'UP'"
            @click.native.stop="onThumbsUp"
          />
          <BaseAvatar
            v-if="tender.tenantTender.thumbsRating === 'UP'"
            class="thumbs-rating__avatar"
            :name="getFullName(tender.tenantTender.thumbsRatingBy)"
            :color="isCurrentUser(tender.tenantTender.thumbsRatingBy) ? '#fcdbd9' : '#d9e5f9'"
            :small="true"
          />
        </div>
        <template v-slot:popover>
          <div class="thumbs-rating--tooltip">
            <BaseIcon name="creation" class="magic__icon" />
            <div class="thumbs-rating--tooltip__title">{{ thumbUpTooltip.title }}</div>
            <div class="thumbs-rating--tooltip__subtitle">
              {{ thumbUpTooltip.subtitle }}
            </div>
          </div>
        </template>
      </v-popover>
    </div>
    <TenderArchiveDialog ref="tenderArchiveDialog" @on-submit="onArchiveTender" />
    <ConfirmationDialog
      ref="tenderRatingOverwriteDialog"
      :buttonText="$t('actions.confirm')"
      @execute="onOverrideTenderRating"
    >
      <template slot="content">
        {{ $t('tender-details.override-tender-rating-dialog.content') }}
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import TenderArchiveDialog from '@/components/page-tender-details/TenderArchiveDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseAvatar from '@/components/base/BaseAvatar.vue';
import { bus } from '@/main';
import { mapActions, mapGetters } from 'vuex';
import { formatShortDate, toMoment } from '@/util/date-util';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';

export default {
  name: 'TenderThumbsRating',
  components: {
    ConfirmationDialog,
    BaseAvatar,
    BaseRoundButton,
    BaseIcon,
    TenderArchiveDialog,
  },
  props: {
    tender: {
      type: Object,
      required: true,
    },
    thumbsUpCssClass: {
      type: String,
      required: false,
    },
    thumbsDownCssClass: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      thumbChoice: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['users']),
    thumbDownTooltip() {
      if (
        this.tender.tenantTender.thumbsRating === 'DOWN' &&
        this.tender.tenantTender.thumbsRatingBy &&
        this.tender.tenantTender.thumbsRatingDate
      ) {
        return {
          title: this.$t('tender-details.thumbs-rating.rated-down-message.title', {
            name: this.getFullName(this.tender.tenantTender.thumbsRatingBy),
            date: formatShortDate(toMoment(this.tender.tenantTender.thumbsRatingDate)),
          }),
          subtitle: this.$t('tender-details.thumbs-rating.rated-down-message.subtitle'),
        };
      }
      return {
        title: this.$tc('tender-details.thumbs-rating.down-message.title', 1),
        subtitle: this.$t('tender-details.thumbs-rating.down-message.subtitle'),
      };
    },
    thumbUpTooltip() {
      if (
        this.tender.tenantTender.thumbsRating === 'UP' &&
        this.tender.tenantTender.thumbsRatingBy &&
        this.tender.tenantTender.thumbsRatingDate
      ) {
        return {
          title: this.$t('tender-details.thumbs-rating.rated-up-message.title', {
            name: this.getFullName(this.tender.tenantTender.thumbsRatingBy),
            date: formatShortDate(toMoment(this.tender.tenantTender.thumbsRatingDate)),
          }),
          subtitle: this.$t('tender-details.thumbs-rating.rated-up-message.subtitle'),
        };
      }
      return {
        title: this.$tc('tender-details.thumbs-rating.up-message.title', 1),
        subtitle: this.$t('tender-details.thumbs-rating.up-message.subtitle'),
      };
    },
  },
  methods: {
    ...mapActions('tender', ['rateUp', 'rateDown', 'unRate', 'archive', 'unarchive']),
    executeThumbsRating() {
      let promise;
      if (this.thumbChoice === 'UP') {
        if (this.tender.tenantTender.thumbsRating === 'UP') {
          promise = this.unRate({ id: this.tender.id });
        } else {
          promise = this.rateUp({ id: this.tender.id });
        }
      } else if (this.thumbChoice === 'DOWN') {
        if (this.tender.tenantTender.thumbsRating === 'DOWN') {
          promise = this.unRate({ id: this.tender.id });
        } else {
          promise = this.rateDown({ id: this.tender.id });
          if (!this.tender.tenantTender.archived) {
            this.$refs['tenderArchiveDialog'].openDialog();
          }
        }
      }
      promise.catch((error) => this.$toast.error(error));
    },
    onThumbsUp() {
      this.thumbChoice = 'UP';
      if (this.tender.tenantTender.thumbsRatingBy && !this.isCurrentUser(this.tender.tenantTender.thumbsRatingBy)) {
        this.$refs['tenderRatingOverwriteDialog'].openDialog();
        return;
      }
      this.executeThumbsRating();
    },
    onThumbsDown() {
      this.thumbChoice = 'DOWN';
      if (this.tender.tenantTender.thumbsRatingBy && !this.isCurrentUser(this.tender.tenantTender.thumbsRatingBy)) {
        this.$refs['tenderRatingOverwriteDialog'].openDialog();
        return;
      }
      this.executeThumbsRating();
    },
    onOverrideTenderRating() {
      this.executeThumbsRating();
      this.$refs['tenderRatingOverwriteDialog'].closeDialog();
    },
    onArchiveTender() {
      this.$track.tenderArchive();
      this.archive({ id: this.tender.id })
        .then(() => {
          this.$toast.cancel(this.$tc('toast.archive'), () =>
            this.unarchive({ id: this.tender.id }).then(() => bus.$emit('refresh-page-requested')),
          );
        })
        .then(() => {
          this.$emit('navigate');
          bus.$emit('refresh-page-requested');
        })
        .catch((error) => this.$toast.error(error));
      this.$refs['tenderArchiveDialog'].closeDialog();
    },
    isCurrentUser(userId) {
      return userId === this.currentUser.id;
    },
    getFullName(authorId) {
      if (this.users) {
        const user = this.users.find((user) => user.id === authorId);
        if (!user) return;
        return user.fullName;
      }
    },
  },
};
</script>

<style lang="scss">
.thumbs-rating {
  &__actions {
    display: flex;
    gap: 12px;
    padding-right: 8px;
  }

  &__action {
    position: relative;
  }

  &__avatar {
    position: absolute;
    bottom: -0.7rem;
    right: -0.55rem;
  }

  &--tooltip {
    text-align: left;

    &__title {
      font-size: 14px;
      padding-bottom: 4px;
    }

    &__subtitle {
      font-size: 12px;
      padding-bottom: 4px;
      color: $color-neutral;
    }
  }
}

.magic__icon {
  height: 16px;
  width: 16px;
}
</style>
